import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader
} from "@/components/ui/dialog";
import { Button } from "../ui/button";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { cn } from "@/utils/styles";
import { getLoginRoute } from "@/constants";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { LoginPanelContent } from "@/components/Auth/LoginPanelContent";

export type RequiresLoginModalProps = {
  isOpen: boolean;
  mode?: string;
  onClose: () => void;
};

export const RequiresLoginModal: React.FC<RequiresLoginModalProps> = ({
  isOpen,
  mode = "default",
  onClose
}) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [location] = useLocation();
  const [cannotClose, pageParams] = useRoute(`${getLoginRoute()}/:section?`);

  const title = useMemo(() => {
    return {
      default: isSignUp ? "Sign up for an account" : "Log into your account",
      intelligence: isSignUp ? "Sign up to continue" : "Log into to continue",
      createCluster: isSignUp ? (
        <>
          Sign up to Start
          <br />
          Your Deployment
        </>
      ) : (
        <>
          Login to Start
          <br />
          Your Deployment
        </>
      )
    }[mode];
  }, [mode, isSignUp]);
  const description = useMemo(() => {
    return {
      default: "You're just one step away",
      createCluster: "You're just one step away",
      intelligence: "You're just one step away"
    }[mode];
  }, [mode]);

  useEffect(() => {
    setIsSignUp(pageParams?.section === "signup");
  }, [pageParams?.section]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setIsSignUp(mode !== "default");

    const pathSegments = location.split("/").filter(Boolean);
    const section = pathSegments[0] ?? "";

    trackEvent(AnalyticsEventType.SIGNUP_CLICKED, {
      screen: section
    });
  }, [isOpen, mode, location]);

  return (
    <Dialog open={isOpen} defaultOpen={isOpen} modal={!cannotClose}>
      <DialogContent
        // bg-[url('/assets/images/bg-login.png')]
        overlayClassName="bg-black/60 backdrop-blur-lg"
        className={cn(
          "custom-scrollbar flex max-h-[100vh] w-[920px] max-w-full flex-col gap-0 overflow-y-auto bg-background bg-[length:927px_659px] bg-right-bottom bg-no-repeat !px-0 py-0 max-md:max-w-[420px] max-md:bg-none lg:min-h-[510px]",
          cannotClose && "!z-[1]"
        )}
        showCloseIcon={false}
        onClose={onClose}
      >
        <DialogHeader className="sr-only">Authentication</DialogHeader>
        <LoginPanelContent
          title={title ?? ""}
          description={description ?? ""}
          isOpen={isOpen}
          isSignUp={isSignUp}
          onChangeIsSignUp={setIsSignUp}
        />

        <DialogFooter className="relative z-[1] flex flex-row items-center !justify-center gap-2 rounded-b-lg border-t border-border bg-background px-6 py-4 dark:border-gray-dark-200">
          <p className="text-sm text-gray-dark-2200">
            By continuing, you agree to IO's{" "}
            <a className="text-foreground underline" href="http://io.net/terms" target="_blank">
              Terms of Service
            </a>{" "}
            and{" "}
            <a className="text-foreground underline" href="http://io.net/privacy" target="_blank">
              Privacy Policy
            </a>
            .
          </p>
          <div className="flex-1"></div>
          {!cannotClose && (
            <Button onClick={onClose} variant="outline" size="sm" className="min-w-[95px]">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
